import { useMemo, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Barra, Card, Etiqueta, Meta, cores } from "../../relatorio/styles";
import { NumeroAnimado } from "../../relatorio/componentes/numero-animado";
import {
  FILTROS_DE_CONTA,
  descricaoDoConvite,
  explicarErro,
  filtrarContas,
  iniciais,
  resumoDasContas,
  resumoDosPerfis,
  situacaoDaConta,
  CORES_KPI,
  type FiltroDeConta,
} from "../pages/contas";
import { desdeQuando } from "../pages/estados";
import { LinkDeAcesso } from "./link-de-acesso";
import { ClienteNovo } from "./cliente-novo";
import { PEDE_ACESSO, usePedidosDeAcesso, type GerarLink } from "../pages/acesso";
import { descricaoDoCliente, situacaoDoCliente } from "../pages/cliente";
import type { CategoriaNoGoogle, ClienteNovo as DadosDoClienteNovo, ContaConectada, EstadoDeAcesso, PedidoDeAcesso, PerfilNoGoogle, PerfilQualidade, Reivindicacao, SituacaoNoGoogle } from "../types";

const Secao = styled.section`
  font-family: var(--font-sans);
  color: ${cores.ink};
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 36px;
  min-width: 0;
  h1 {
    font-family: var(--font-sans);
    font-size: 32px;
    line-height: 40px;
    font-weight: 400;
    letter-spacing: 0;
    margin: 0;
    color: var(--ink);
  }
`;

const Kpis = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 20px;
  }
`;

/* Cartão de KPI da prancheta: rótulo colorido + ícone, número grande escuro, texto de apoio. */
const Kpi = styled.div<{ $fundo?: string }>`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  .topo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .topo .material-icons {
    font-size: 22px;
  }
  .numero {
    font-family: var(--font-sans);
    font-size: 44px;
    line-height: 48px;
    font-weight: 500;
    color: var(--ink);
    letter-spacing: -1px;
  }
  .apoio {
    font-size: 13px;
    color: #3c4043;
  }
`;

const Rotulo = styled.span<{ $cor?: string }>`
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  color: ${({ $cor }) => $cor ?? cores.ink3};
`;

const Ferramentas = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  label {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 1 420px;
    min-width: 220px;
    height: 44px;
    padding: 0 16px;
    border: 1px solid #dadce0;
    border-radius: 999px;
    background: #fff;
    box-sizing: border-box;
    color: #5f6368;
    &:focus-within {
      border-color: var(--brand);
      box-shadow: 0 0 0 1px var(--brand);
    }
  }
  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    font: inherit;
    font-size: 14px;
    color: var(--ink);
    background: transparent;
  }
`;

const Linha = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  padding: 20px 0;
  border-top: 1px solid #f1f3f4;
  &:first-child {
    border-top: 0;
  }
  @media (min-width: 1000px) {
    grid-template-columns: 44px minmax(0, 1.4fr) minmax(0, 1fr) 300px;
    gap: 24px;
    align-items: center;
  }
`;

const Avatar = styled.div<{ $cor: string; $fundo: string }>`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: ${({ $fundo }) => $fundo};
  color: ${({ $cor }) => $cor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 14px;
`;

/* "Ver convites" tonal, "Verificar convites" outlined (prancheta QualidadePerfis). */
const Botaozinho = styled.button<{ $cor?: string; $tonal?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  border: 1px solid ${({ $tonal }) => ($tonal ? "transparent" : "#dadce0")};
  background: ${({ $tonal }) => ($tonal ? "var(--brand-50)" : "#fff")};
  color: ${({ $cor, $tonal }) => $cor ?? ($tonal ? "var(--brand-700)" : "var(--brand-700)")};
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  border-radius: 10px;
  padding: 0 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${({ $tonal }) => ($tonal ? "var(--brand-100)" : "#f8fafd")};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Detalhe = styled.div`
  padding: 0 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (min-width: 1000px) {
    padding-left: 58px;
  }
`;

const ItemDePerfil = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${cores.line};
  font-size: 14px;
  &:first-child {
    border-top: 0;
  }
  a {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
  }
`;

const SITUACOES: { chave: EstadoDeAcesso; rotulo: string; cor: keyof typeof CORES_KPI; contagem: (c: ContaConectada) => number }[] = [
  { chave: "SEM_ACESSO", rotulo: "sem acesso", cor: "vermelha", contagem: (c) => c.semAcesso },
  { chave: "SUSPENSO", rotulo: "suspensos", cor: "amarela", contagem: (c) => c.suspensos },
  { chave: "COPIA", rotulo: "cópias", cor: "roxa", contagem: (c) => c.copias },
  { chave: "ACESSO_SOLICITADO", rotulo: "acesso solicitado", cor: "azul", contagem: (c) => c.acessoSolicitado },
];

type Props = {
  contas: ContaConectada[];
  carregando: boolean;
  verificando: number | "todas" | null;
  onVerificar: (userId: number) => void;
  onVerificarTodas: () => void;
  onListarPerfis: (email: string, estado: EstadoDeAcesso) => Promise<PerfilQualidade[]>;
  onPedirAcesso: (publicId: string) => Promise<boolean>;
  onGerarLink: GerarLink;
  clienteNovo: {
    buscarPlaces: (texto: string) => Promise<{ lista: PerfilNoGoogle[]; erro: string | null }>;
    gerarLink: (cliente: DadosDoClienteNovo) => Promise<PedidoDeAcesso | null>;
    listarPedidos: () => Promise<PedidoDeAcesso[]>;
    situacaoNoGoogle: (dados: { placeId: string | null; name: string; address: string; managerUserId: number }) => Promise<SituacaoNoGoogle | null>;
    reivindicar: (cliente: DadosDoClienteNovo) => Promise<Reivindicacao | null>;
    opcoesDeVerificacao: (tokenDoPedido: string) => Promise<Reivindicacao | null>;
    iniciarVerificacao: (tokenDoPedido: string, dados: { method: string; phoneNumber?: string | null; emailAddress?: string | null }) => Promise<PedidoDeAcesso | null>;
    concluirVerificacao: (tokenDoPedido: string, pin: string) => Promise<PedidoDeAcesso | null>;
    buscarCategorias: (texto: string) => Promise<CategoriaNoGoogle[]>;
  };
};

export const ContasConectadas = ({ contas, carregando, verificando, onVerificar, onVerificarTodas, onListarPerfis, onPedirAcesso, onGerarLink, clienteNovo }: Props) => {
  const { pedidoDe, gerar, gerando } = usePedidosDeAcesso(onGerarLink);
  const [clienteNovoAberto, setClienteNovoAberto] = useState(false);
  const [filtro, setFiltro] = useState<FiltroDeConta>("TODAS");
  const [busca, setBusca] = useState("");
  const [convitesAbertos, setConvitesAbertos] = useState<number | null>(null);
  const [perfisAbertos, setPerfisAbertos] = useState<{ userId: number; estado: EstadoDeAcesso; lista: PerfilQualidade[]; carregando: boolean } | null>(null);
  const resumo = useMemo(() => resumoDasContas(contas), [contas]);
  const visiveis = useMemo(() => filtrarContas(contas, filtro, busca), [contas, filtro, busca]);
  const ultimaChecagem = useMemo(
    () => contas.map((c) => c.invitationsCheckedAt).filter(Boolean).sort().at(-1) ?? null,
    [contas],
  );

  const abrirPerfis = async (conta: ContaConectada, estado: EstadoDeAcesso) => {
    if (perfisAbertos?.userId === conta.userId && perfisAbertos.estado === estado) {
      setPerfisAbertos(null);
      return;
    }
    setPerfisAbertos({ userId: conta.userId, estado, lista: [], carregando: true });
    const lista = await onListarPerfis(conta.email, estado);
    setPerfisAbertos((atual) => (atual?.userId === conta.userId && atual.estado === estado ? { ...atual, lista, carregando: false } : atual));
  };

  const pedirAcesso = async (publicId: string) => {
    if (await onPedirAcesso(publicId)) {
      setPerfisAbertos((atual) => (atual ? { ...atual, lista: atual.lista.filter((perfil) => perfil.publicId !== publicId) } : atual));
    }
  };

  return (
    <Secao aria-label="Contas conectadas">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h1>Saúde das contas conectadas</h1>
          <Meta style={{ fontSize: 14, lineHeight: "20px" }}>
            Contas com problema aparecem primeiro, ordenadas pelo número de perfis afetados. Reconectar é a ação que destrava a sincronização.
            {ultimaChecagem ? ` Convites conferidos ${desdeQuando(ultimaChecagem)}.` : " Convites ainda não conferidos."}
          </Meta>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <BotaoDoTopo type="button" onClick={onVerificarTodas} disabled={verificando !== null}>
            <span className="material-icons">fact_check</span>
            {verificando === "todas" ? "Verificando…" : "Verificar convites em todas"}
          </BotaoDoTopo>
          <BotaoDoTopo
            type="button"
            $cheio={!clienteNovoAberto}
            onClick={() => setClienteNovoAberto((atual) => !atual)}
            aria-expanded={clienteNovoAberto}
          >
            <span className="material-icons">{clienteNovoAberto ? "close" : "person_add"}</span>
            {clienteNovoAberto ? "Fechar cliente novo" : "Pegar acesso de cliente novo"}
          </BotaoDoTopo>
        </div>
      </div>

      {clienteNovoAberto && (
        <ClienteNovo contas={contas} onBuscarPlaces={clienteNovo.buscarPlaces} onGerarLink={clienteNovo.gerarLink} onListarPedidos={clienteNovo.listarPedidos} onSituacaoNoGoogle={clienteNovo.situacaoNoGoogle} verificacao={{ reivindicar: clienteNovo.reivindicar, opcoes: clienteNovo.opcoesDeVerificacao, iniciar: clienteNovo.iniciarVerificacao, concluir: clienteNovo.concluirVerificacao, buscarCategorias: clienteNovo.buscarCategorias }} />
      )}

      <Kpis>
        <Kpi>
          <div className="topo"><Rotulo $cor={CORES_KPI.verde.cor}>Contas conectadas</Rotulo><span className="material-icons" style={{ color: CORES_KPI.verde.cor }}>link</span></div>
          <div className="numero"><NumeroAnimado valor={resumo.conectadas} /></div>
          <div className="apoio">{`de ${contas.length - resumo.semToken} contas que já conectaram`}</div>
        </Kpi>
        <Kpi>
          <div className="topo"><Rotulo $cor={CORES_KPI.vermelha.cor}>Precisam reconectar</Rotulo><span className="material-icons" style={{ color: CORES_KPI.vermelha.cor }}>link_off</span></div>
          <div className="numero"><NumeroAnimado valor={resumo.reconectar} /></div>
          <div className="apoio">{resumo.semToken > 0 ? `token recusado pelo Google · ${resumo.semToken} nunca conectaram` : "token recusado pelo Google"}</div>
        </Kpi>
        <Kpi>
          <div className="topo"><Rotulo $cor={CORES_KPI.amarela.cor}>Convites pendentes</Rotulo><span className="material-icons" style={{ color: CORES_KPI.amarela.cor }}>mark_email_unread</span></div>
          <div className="numero"><NumeroAnimado valor={resumo.convites} /></div>
          <div className="apoio">alguém nos chamou para gerenciar uma ficha</div>
        </Kpi>
        <Kpi>
          <div className="topo"><Rotulo $cor={CORES_KPI.azul.cor}>Perfis administrados</Rotulo><span className="material-icons" style={{ color: CORES_KPI.azul.cor }}>storefront</span></div>
          <div className="numero"><NumeroAnimado valor={resumo.perfis} /></div>
          <div className="apoio">{`${resumo.comProblema.toLocaleString("pt-BR")} com problema de acesso`}</div>
        </Kpi>
      </Kpis>

      <Ferramentas>
        <label>
          <span className="material-icons" aria-hidden="true">search</span>
          <input
            type="search"
            placeholder="Buscar conta por e-mail ou nome"
            aria-label="Buscar conta"
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
          />
        </label>
        <div role="tablist" aria-label="Filtro de contas" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {FILTROS_DE_CONTA.map((item) => (
            <ChipDeFiltro key={item.chave} type="button" role="tab" aria-selected={filtro === item.chave} $ativo={filtro === item.chave} onClick={() => setFiltro(item.chave)}>
              {PONTO_DO_FILTRO[item.chave] && <span className="dot" style={{ background: PONTO_DO_FILTRO[item.chave] }} />}
              {item.rotulo}
            </ChipDeFiltro>
          ))}
        </div>
      </Ferramentas>

      <CartaoDaLista>
        <CabecalhoDaLista>
          <span />
          <span>Conta gestora</span>
          <span>Perfis e acesso</span>
          <span style={{ textAlign: "right" }}>Ações</span>
        </CabecalhoDaLista>
        {carregando && <Meta style={{ display: "block", padding: "14px 0" }}>Carregando contas…</Meta>}
        {!carregando && visiveis.length === 0 && <Meta style={{ display: "block", padding: "14px 0" }}>Nenhuma conta nesta situação.</Meta>}
        {visiveis.map((conta) => {
          const situacao = situacaoDaConta(conta);
          const tema = CORES_KPI[situacao.cor];
          const perfis = resumoDosPerfis(conta);
          const erro = explicarErro(conta.invitationsError);
          const convitesVisiveis = convitesAbertos === conta.userId;
          const situacoesComPerfis = SITUACOES.filter((s) => s.contagem(conta) > 0);
          const detalhe = perfisAbertos?.userId === conta.userId ? perfisAbertos : null;
          return (
            <div key={conta.userId}>
              <Linha>
                <Avatar $cor={tema.cor} $fundo={tema.fundo}>{iniciais(conta.name, conta.email)}</Avatar>
                <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 15, color: "#202124", overflowWrap: "anywhere" }}>{conta.email}</span>
                    <Etiqueta $cor={situacao.cor}>{situacao.rotulo}</Etiqueta>
                    {conta.invitationsPending > 0 && (
                      <Etiqueta $cor="amarela">{`${conta.invitationsPending} ${conta.invitationsPending === 1 ? "convite pendente" : "convites pendentes"}`}</Etiqueta>
                    )}
                  </div>
                  <Meta>
                    {conta.name && conta.name !== conta.email ? `${conta.name} · ` : ""}
                    {conta.tokenSince ? `conectada ${desdeQuando(conta.tokenSince)}` : "nunca conectada"}
                  </Meta>
                  {erro && (
                    <Meta style={{ color: "#C5221F", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span className="material-icons" style={{ fontSize: 16 }}>error</span>
                      {erro}
                    </Meta>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <Meta>{`${conta.profiles.toLocaleString("pt-BR")} ${conta.profiles === 1 ? "perfil" : "perfis"}`}</Meta>
                    <Meta style={{ color: perfis.alerta ? CORES_KPI.vermelha.cor : cores.ink4, fontFamily: fonts.gilroySemiBold }}>{perfis.texto}</Meta>
                  </div>
                  <Barra $largura={conta.profiles ? (conta.comAcesso / conta.profiles) * 100 : 0} $cor={CORES_KPI.verde.cor} $atraso={0.2} />
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {situacoesComPerfis.length === 0 && <Meta>{conta.profiles > 0 ? `${conta.comAcesso} com acesso` : "—"}</Meta>}
                    {situacoesComPerfis.map((s) => (
                      <Mini
                        key={s.chave}
                        type="button"
                        aria-pressed={detalhe?.estado === s.chave}
                        onClick={() => abrirPerfis(conta, s.chave)}
                        style={{ color: CORES_KPI[s.cor].cor, borderColor: CORES_KPI[s.cor].borda, background: detalhe?.estado === s.chave ? CORES_KPI[s.cor].fundo : "#fff" }}
                        title={`Ver os perfis ${s.rotulo} desta conta`}
                      >
                        {`${s.contagem(conta)} ${s.rotulo}`}
                        <span className="material-icons" style={{ fontSize: 14 }}>chevron_right</span>
                      </Mini>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {conta.invitationsPending > 0 && (
                    <Botaozinho type="button" $tonal onClick={() => setConvitesAbertos(convitesVisiveis ? null : conta.userId)}>
                      {convitesVisiveis ? "Ocultar convites" : "Ver convites"}
                    </Botaozinho>
                  )}
                  <Botaozinho type="button" onClick={() => onVerificar(conta.userId)} disabled={verificando !== null || !conta.hasToken}>
                    {verificando === conta.userId ? "Consultando…" : "Verificar convites"}
                  </Botaozinho>
                </div>
              </Linha>
              {convitesVisiveis && conta.invitations.length > 0 && (
                <Detalhe>
                  {conta.invitations.map((convite) => (
                    <Card key={convite.invitationName} $fundo={CORES_KPI.amarela.fundo} $compacto style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14 }}>{descricaoDoConvite(convite)}</span>
                      <Meta>
                        {[convite.targetAddress, convite.accountTitle ? `conta ${convite.accountTitle}` : null, convite.checkedAt ? `visto ${desdeQuando(convite.checkedAt)}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </Meta>
                      <Meta>Para aceitar, entre no Perfil da Empresa com este e-mail e aceite o convite.</Meta>
                    </Card>
                  ))}
                </Detalhe>
              )}
              {detalhe && (
                <Detalhe>
                  <Card $fundo={cores.grayTint} style={{ padding: "6px 18px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "10px 0 4px" }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14 }}>
                        {`Perfis ${SITUACOES.find((s) => s.chave === detalhe.estado)?.rotulo} · ${conta.email}`}
                      </span>
                      <Meta>{detalhe.carregando ? "carregando…" : `${detalhe.lista.length} ${detalhe.lista.length === 1 ? "perfil" : "perfis"}`}</Meta>
                    </div>
                    {!detalhe.carregando && detalhe.lista.length === 0 && <Meta style={{ display: "block", padding: "8px 0" }}>Nenhum perfil nesta situação.</Meta>}
                    {detalhe.lista.map((perfil) => (
                      <ItemDePerfil key={perfil.publicId}>
                        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                          <span>
                            <a href={`/dashboard/${perfil.publicId}`} target="_blank" rel="noopener noreferrer">{perfil.name}</a>
                            <Meta>{perfil.city ? ` — ${perfil.city}/${perfil.state ?? ""}` : ""}{` · conferido ${desdeQuando(perfil.accessCheckedAt)}`}</Meta>
                          </span>
                          {(descricaoDoCliente(perfil) || situacaoDoCliente(perfil.customerStatus)) && (
                            <Meta>
                              {descricaoDoCliente(perfil) ? `Cliente: ${descricaoDoCliente(perfil)}` : ""}
                              {situacaoDoCliente(perfil.customerStatus) ? ` · ${situacaoDoCliente(perfil.customerStatus)}` : ""}
                            </Meta>
                          )}
                        </span>
                        {PEDE_ACESSO.includes(detalhe.estado) && (
                          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                            <LinkDeAcesso pedido={pedidoDe(perfil)} gerando={gerando === perfil.publicId} onGerar={() => gerar(perfil.publicId)} />
                            {detalhe.estado === "SEM_ACESSO" && (
                              <Botaozinho type="button" $cor={cores.ink3} onClick={() => pedirAcesso(perfil.publicId)}>Marcar acesso solicitado</Botaozinho>
                            )}
                          </span>
                        )}
                      </ItemDePerfil>
                    ))}
                  </Card>
                </Detalhe>
              )}
            </div>
          );
        })}
      </CartaoDaLista>

      <Meta>
        O que o Google mostra por API: conexão da conta, situação de cada perfil e convites recebidos. Pedidos de propriedade feitos por terceiros e os avisos por e-mail não são expostos pela API — chegam só na caixa de entrada de cada conta.
      </Meta>
    </Secao>
  );
};


/* Cabeçalho da lista e cartão da lista no padrão do redesign. */
const CabecalhoDaLista = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1.4fr) minmax(0, 1fr) 300px;
  gap: 24px;
  padding: 14px 0 12px;
  border-bottom: 1px solid var(--line);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: var(--text-muted);
  text-transform: uppercase;
  @media (max-width: 999px) {
    display: none;
  }
`;

const CartaoDaLista = styled.div`
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 0 28px;
  min-width: 0;
`;

const ChipDeFiltro = styled.button<{ $ativo?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ $ativo }) => ($ativo ? "#202124" : "#dadce0")};
  background: ${({ $ativo }) => ($ativo ? "#202124" : "#ffffff")};
  color: ${({ $ativo }) => ($ativo ? "#ffffff" : "#3c4043")};
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    display: inline-block;
  }
`;

const BotaoDoTopo = styled.button<{ $cheio?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid ${({ $cheio }) => ($cheio ? "transparent" : "#dadce0")};
  background: ${({ $cheio }) => ($cheio ? "var(--brand)" : "#ffffff")};
  color: ${({ $cheio }) => ($cheio ? "#ffffff" : "var(--brand-700)")};
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${({ $cheio }) => ($cheio ? "var(--brand-700)" : "#f8fafd")};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .material-icons {
    font-size: 20px;
  }
`;

const PONTO_DO_FILTRO: Partial<Record<FiltroDeConta, string>> = {
  RECONECTAR: "#D93025",
  COM_CONVITES: "#F9AB00",
};

const Mini = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #dadce0;
  background: #fff;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  color: #3c4043;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #f1f3f4;
  }
`;
